/**
 * Внешний вид: тема, звук ошибки и игровые элементы (ТЗ UX-01, FR-GM-08).
 *
 * Живёт внутри раскрываемых настроек, а не в шапке: пять кнопок, стоявших на
 * каждом экране, стоили строки, которой не хватало тексту упражнения
 * (ТЗ FR-EN-83). Фокус-режим сюда не входит — он остался кнопкой шапки:
 * его включают и выключают посреди занятия, и прятать его в панель значит
 * заставлять открывать её каждый раз.
 */

import type { Translate } from '@type2x/i18n';
import type { ThemePreference } from '@type2x/shared-contracts';
import { THEME_PREFERENCES } from '@type2x/shared-contracts';
import { Button } from '@type2x/ui';

export interface AppearanceBarProps {
  readonly t: Translate;
  readonly theme: ThemePreference;
  /** Звук ошибки выключается отсюда (ТЗ UX-10): по умолчанию он выключен. */
  readonly soundOnError: boolean;
  readonly gameElements: boolean;
  readonly onThemeChange: (theme: ThemePreference) => void;
  readonly onSoundOnErrorChange: (soundOnError: boolean) => void;
  readonly onGameElementsChange: (gameElements: boolean) => void;
}

export function AppearanceBar({
  t,
  theme,
  soundOnError,
  gameElements,
  onThemeChange,
  onSoundOnErrorChange,
  onGameElementsChange,
}: AppearanceBarProps): React.JSX.Element {
  return (
    <div className="t2x-appearance">
      <div className="t2x-appearance__themes" role="group" aria-label={t('appearance.theme')}>
        {THEME_PREFERENCES.map((option) => (
          <Button
            key={option}
            variant={option === theme ? 'primary' : 'ghost'}
            onClick={() => onThemeChange(option)}
            // Выбранная тема отмечена не только цветом кнопки: цвет здесь
            // единственным носителем смысла быть не может (UX-12).
            aria-pressed={option === theme}
          >
            {t(`appearance.theme.${option}`)}
          </Button>
        ))}
      </div>

      {/* Состояние звука названо словом, а не выведено из вида кнопки:
          выключенный звук ничем себя не выдаёт, и «включить» на кнопке
          читалось бы как «сейчас включён». */}
      <Button
        variant="ghost"
        onClick={() => onSoundOnErrorChange(!soundOnError)}
        aria-pressed={soundOnError}
      >
        {t(soundOnError ? 'appearance.soundOn' : 'appearance.soundOff')}
      </Button>

      {/* Игровые элементы выключаются целиком (ТЗ FR-GM-08). Выключатель
          стоит здесь, а не на экране достижений: оттуда его было бы не
          вернуть — экран исчезает вместе с ними. */}
      <Button
        variant="ghost"
        onClick={() => onGameElementsChange(!gameElements)}
        aria-pressed={gameElements}
      >
        {t(gameElements ? 'appearance.gameOn' : 'appearance.gameOff')}
      </Button>
    </div>
  );
}
